import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import logo from '../../assets/home_logo.png';
import classNames from 'classnames';

import { faXTwitter, faGithub } from "@fortawesome/free-brands-svg-icons";
import { faBars, faXmark, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useConfig from '../../useConfig';

const NavLink = ({ className, to, children, newTab, onClick, forceActive }) => {
  const location = useLocation();
  const isActive = forceActive !== undefined ? forceActive : location.pathname === to;

  const classes = classNames(
    'px-4 py-2 font-body text-sm transition-colors duration-150 hover:text-primary-light cursor-pointer',
    isActive ? 'text-primary-light' : 'text-neutral-50',
    className
  );
  return (
    <li className={classes}>
    { newTab ? (
        <Link target="_blank" to={to} onClick={onClick}>{children}</Link>
      ) : (
        <Link to={to} onClick={onClick}>{children}</Link>
      )
    }
    </li>
  )
}

const SubNav = ({ title, onEnter, onLeave, isHidden, isActive, children }) => {
  const hide = isHidden ? 'hidden' : '';
  const dropdownClasses = classNames('absolute top-full left-0 z-50', hide);

  return (
    <li
      className={classNames(
        'relative px-4 py-5 font-body text-sm transition-colors duration-150 hover:text-primary-light cursor-pointer',
        isActive ? 'text-primary-light' : 'text-neutral-50'
      )}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <Link to='#'>{title}</Link>
      <div className={dropdownClasses}>
        <ul className='mt-[1px] rounded-b-lg border border-neutral-800 border-t-primary-light bg-neutral-950 py-2 shadow-lg'>
          {children}
        </ul>
      </div>
    </li>
  )
}

// One dropdown on mobile: a heading that toggles its links open in place.
const MobileSubNav = ({ title, links, isOpen, onToggle, onNavigate }) => {
  const location = useLocation();
  const isActive = links.some(({ to }) => location.pathname === to);

  return (
    <li className='flex flex-col items-center'>
      <button
        onClick={onToggle}
        className={classNames(
          'flex items-center gap-3 px-4 py-4 font-heading text-3xl transition-colors duration-150 hover:text-primary-light cursor-pointer',
          isActive ? 'text-primary-light' : 'text-neutral-200'
        )}
      >
        {title}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={classNames('text-lg transition-transform duration-200', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen && (
        <ul className='flex flex-col items-center'>
          {links.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} onClick={onNavigate} className={classNames(
                'block px-6 py-2 font-body text-lg transition-colors duration-150 hover:text-primary-light cursor-pointer',
                location.pathname === to ? 'text-primary-light' : 'text-neutral-400'
              )}>
                {label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

const MobileNavLink = ({ to, children, newTab, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <li>
      { newTab ? (
        <Link
          target="_blank"
          to={to}
          onClick={onClick}
          className={classNames(
            'block px-4 py-4 font-heading text-3xl transition-colors duration-150 hover:text-primary-light',
            isActive ? 'text-primary-light' : 'text-neutral-200'
          )}
        >
          {children}
        </Link>
      ) : (
        <Link
          to={to}
          onClick={onClick}
          className={classNames(
            'block px-4 py-4 font-heading text-3xl transition-colors duration-150 hover:text-primary-light',
            isActive ? 'text-primary-light' : 'text-neutral-200'
          )}
        >
          {children}
        </Link>
      )}
    </li>
  )
}

export const SocialMediaLinks = ({className}) => {
  const classes = classNames('flex flex-row items-center gap-2', className)
  return (
    <ul className={classes}>
      <p className='font-body text-sm text-neutral-400'>Follow Code Scribes</p>
      <NavLink to='https://github.com/hop-along-polly' newTab={true} forceActive={false} className='!text-neutral-400 hover:!text-neutral-50'>
        <FontAwesomeIcon icon={faGithub} size='lg'/>
      </NavLink>
      <NavLink to='https://twitter.com/D3r3kdrumm0nd' newTab={true} forceActive={false} className='!text-neutral-400 hover:!text-neutral-50'>
        <FontAwesomeIcon icon={faXTwitter} size='lg' />
      </NavLink>
    </ul>
  )
}

// Both the desktop dropdowns and the mobile menu are built from these, so the
// two can't disagree about what's in each menu.
const SERVICES = {
  title: 'Services',
  links: [
    { to: '/fractional-cto', label: 'Fractional CTO' },
    { to: '/engineering-sprints', label: 'Engineering Sprints' }
  ]
};

const SUB_NAVS = [
  {
    title: 'Projects',
    links: [
      { to: '/projects/scribe-cards', label: 'Scribe Cards' },
      { to: '/projects/recapture', label: 'REcapture' }
    ]
  }
];

export const NavBar = () => {
  // The title of whichever dropdown is open, or null. Desktop and mobile each
  // track their own so hovering one never opens the other.
  const [openSubNav, setOpenSubNav] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileSubNav, setOpenMobileSubNav] = useState(null);
  const location = useLocation();

  // With the Fractional CTO page hidden, Engineering Sprints is the only
  // service, so it gets a plain link in place of the Services menu.
  const showFractionalCto = useConfig()?.showFractionalCtoPage;
  const subNavs = showFractionalCto ? [SERVICES, ...SUB_NAVS] : SUB_NAVS;

  const closeMobile = () => { setMobileOpen(false); setOpenMobileSubNav(null); };
  const toggleMobileSubNav = (title) => setOpenMobileSubNav(openMobileSubNav === title ? null : title);

  return (
    <>
      <nav className='sticky top-0 z-50 h-16 bg-ink border-b border-neutral-800'>
        <div className='flex flex-row items-center justify-between h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-body'>
          {/* Logo */}
          <Link to='/' className='flex-shrink-0'>
            <img src={logo} className='h-10' alt='Homepage' />
          </Link>

          {/* Desktop nav. Academy is left out until it launches; its route
              still exists. */}
          <ul className='hidden md:flex flex-row items-center flex-1 ml-4 h-full'>
            {!showFractionalCto && <NavLink to='/engineering-sprints'>Engineering Sprints</NavLink>}
            {subNavs.map(({ title, links }) => (
              <SubNav
                key={title}
                onEnter={() => setOpenSubNav(title)}
                onLeave={() => setOpenSubNav(null)}
                title={title}
                isHidden={openSubNav !== title}
                isActive={links.some(({ to }) => location.pathname === to)}
              >
                {links.map(({ to, label }) => (
                  <NavLink key={to} to={to} className='whitespace-nowrap'>{label}</NavLink>
                ))}
              </SubNav>
            ))}
            <NavLink to='/merch'>Merch</NavLink>
          </ul>

          {/* Desktop social links */}
          <SocialMediaLinks className='hidden md:flex' />

          {/* Hamburger button */}
          <button
            className='md:hidden p-2 text-neutral-50 hover:text-primary-light transition-colors duration-150 active:bg-transparent focus:bg-transparent'
            onClick={() => setMobileOpen(true)}
            aria-label='Open navigation menu'
          >
            <FontAwesomeIcon icon={faBars} size='lg' />
          </button>
        </div>
      </nav>

      {/* Full-page mobile menu */}
      <div
        className={classNames(
          'fixed inset-0 z-[100] bg-ink flex flex-col transition-transform duration-300 ease-in-out md:hidden',
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        aria-hidden={!mobileOpen}
      >
          {/* Header with logo and close button */}
          <div className='flex items-center justify-between h-16 px-4 border-b border-neutral-800'>
            <Link to='/' onClick={closeMobile} className='flex-shrink-0'>
              <img src={logo} className='h-10' alt='Homepage' />
            </Link>
            <button
              className='p-2 text-neutral-50 hover:text-primary-light transition-colors duration-150'
              onClick={closeMobile}
              aria-label='Close navigation menu'
            >
              <FontAwesomeIcon icon={faXmark} size='xl' />
            </button>
          </div>

          {/* Mobile nav links */}
          <ul className='flex flex-col items-center justify-center flex-1 gap-2'>
            <MobileNavLink to='/' onClick={closeMobile}>Home</MobileNavLink>
            {!showFractionalCto && <MobileNavLink to='/engineering-sprints' onClick={closeMobile}>Engineering Sprints</MobileNavLink>}
            {subNavs.map(({ title, links }) => (
              <MobileSubNav
                key={title}
                title={title}
                links={links}
                isOpen={openMobileSubNav === title}
                onToggle={() => toggleMobileSubNav(title)}
                onNavigate={closeMobile}
              />
            ))}
            <MobileNavLink to='/merch' onClick={closeMobile}>Merch</MobileNavLink>
          </ul>

          {/* Social links at bottom */}
          <div className='flex justify-center pb-8'>
            <SocialMediaLinks />
          </div>
        </div>
    </>
  );
};

export default NavBar
